import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { getBookmarkedDiaries, toggleDiaryLike, toggleDiaryBookmark, translateDiary, ITranslateResponse } from "api/agency/v2/diary.api";
import { getImageUrl } from "helpers/Image.helper";
import { formatTimeAgo, formattedDateTimeForLocale } from "helpers/Datetime.helper";
import { getStudentNickname, getStudentName, getCurrentLangName, getFlagByCountryCode } from "helpers/String.helper";
import { IPagination, ILikeToggleResponse, IBookmarkToggleResponse } from "api/interfaces/common.interface";
import LoadingComponent from "commons/loading/Loading.component";
import SkeletonLoading from "commons/loading/SkeletonLoading.component";
import { getLanguageValue } from "locales/i18n";
import { PAGE_TYPE } from "enums";
import VideoPlayer from "commons/video/VideoPlayer";
import { usePopup } from "contexts/PopupContext";
import StudentProfilePopup from "pages/agency/v2/popup/StudentProfilePopup";
import ImageSlidePopup from "pages/common/v2/ImageSlidePopup";
import StudentDiaryDetailPopup from "pages/agency/v2/popup/StudentDiaryDetailPopup";

// 날짜별 그룹핑 함수
const groupDiariesByDate = (diaries: any[]) => {
    const groups: { [key: string]: any[] } = {};
    
    diaries.forEach(diary => {
        const date = new Date(diary.createdAt).toISOString().split('T')[0];
        if (!groups[date]) {
            groups[date] = [];
        }
        groups[date].push(diary);
    });
    
    return Object.keys(groups)
        .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())
        .map(date => ({
            date,
            diaries: groups[date].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        }));
};

// 날짜 헤더 컴포넌트
const DateHeader: React.FC<{ date: string }> = ({ date }) => {
    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        
        // 날짜 비교를 위해 시간 부분 제거
        const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
        const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        const yesterdayOnly = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate());
        
        if (dateOnly.getTime() === todayOnly.getTime()) {
            return "오늘";
        } else if (dateOnly.getTime() === yesterdayOnly.getTime()) {
            return "어제";
        } else {
            const formattedDate = date.toLocaleDateString('ko-KR', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            });
            const weekday = date.toLocaleDateString('ko-KR', {
                weekday: 'short'
            });
            return `${formattedDate} (${weekday})`;
        }
    };

    return (
        <div className="flex items-center py-2">
            <div className="flex-1 border-t border-gray-100"></div>
            <div className="px-3 py-1">
                <span className="text-xs text-gray-400">
                    {formatDate(date)}
                </span>
            </div>
            <div className="flex-1 border-t border-gray-100"></div>
        </div>
    );
};

// 다이어리 아이템 컴포넌트
const DiaryItem: React.FC<{
    diary: any;
    onStudentInfoPopup: (userStudentId: number) => void;
    onImageClick: (images: any[], initialIndex: number) => void;
    onDiaryDetailPopup: (diaryId: number) => void;
    onLikeToggle: (diaryId: number) => void;
    onBookmarkToggle: (diaryId: number) => void;
    // 번역 관련 props
    isTranslating?: boolean;
    translatedData?: ITranslateResponse;
    onTranslate?: (diaryId: number) => void;
    // 로딩 상태 props
    isLiking?: boolean;
    isBookmarking?: boolean;
}> = ({ diary, onStudentInfoPopup, onImageClick, onDiaryDetailPopup, onLikeToggle, onBookmarkToggle, isTranslating = false, translatedData, onTranslate, isLiking = false, isBookmarking = false }) => {
    const { showPopup } = usePopup();

    return (
        <div className="bg-white border border-gray-200 rounded-lg p-3 hover:shadow-sm transition-shadow cursor-pointer">
            {/* 다이어리 헤더 */}
            <div className="flex items-center justify-between mb-2">
                <div 
                    className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 rounded-lg p-1 -m-1 transition-colors"
                    onClick={() => onStudentInfoPopup(diary.user?.userStudent?.id)}
                >
                    <div className="w-8 h-8 rounded-full overflow-hidden">
                        <img
                            src={getImageUrl(diary.user?.userStudent?.profileImage, 80, 80)}
                            alt="Student Profile"
                            className="w-full h-full object-cover"
                            onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.src = "/imgs/dummy/basic-img.png";
                            }}
                        />
                    </div>
                    <div>
                        <h3 className="font-semibold text-gray-800 text-sm">
                            {getStudentName(diary.user?.userStudent)} ({getStudentNickname(diary.user?.userStudent, '')})
                        </h3>
                        <p className="text-xs text-gray-400">
                            {formattedDateTimeForLocale(diary.createdAt, 'ko-KR')}
                        </p>
                    </div>
                </div>

                <div className="flex items-center space-x-2">
                    {/* 포트폴리오 배지 */}
                    {diary.isPortfolio && (
                        <div className="flex items-center gap-1 px-2 py-1 bg-[#CF3688]/10 text-[#CF3688] rounded-full text-xs font-medium">
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            포트폴리오
                        </div>
                    )}

                    {/* 번역하기 버튼 */}
                    {onTranslate && (
                        <div
                            className={`flex flex-col items-center space-y-1 transition-all duration-200 ${isTranslating
                                ? 'opacity-50 cursor-not-allowed'
                                : 'cursor-pointer hover:opacity-80'
                                }`}
                            onClick={(e) => {
                                e.stopPropagation();
                                if (!isTranslating) onTranslate(diary.id);
                            }}
                        >
                            {isTranslating ? (
                                // 로딩 중일 때 스피너 표시
                                <svg className="w-4 h-4 text-gray-400 animate-spin" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                            ) : (
                                // 번역 아이콘
                                <img 
                                    src={translatedData ? "/imgs/icons/icon-translate-active.svg" : "/imgs/icons/icon-translate.svg"} 
                                    alt="translate" 
                                    className="w-4 h-4" 
                                />
                            )}
                            <span className={`text-xs font-medium ${translatedData ? 'text-[#CF3688]' : 'text-gray-400'}`}>
                                번역
                            </span>
                        </div>
                    )}
                </div>
            </div>

            {/* 다이어리 제목 */}
            <div className="mb-1">
                <h4 className="font-bold text-gray-900 text-sm">
                    {diary.title}
                </h4>
                {translatedData && (
                    <h4 className="font-bold text-gray-500 text-sm mt-1 pl-3 border-l-2 border-gray-400">
                        {translatedData.translatedTitle}
                    </h4>
                )}
            </div>

            {/* 다이어리 내용 */}
            <div className="mb-2">
                <p className="text-gray-600 text-xs line-clamp-2 leading-relaxed">
                    {diary.content}
                </p>
                {translatedData && (
                    <p className="text-gray-500 text-xs line-clamp-2 leading-relaxed mt-1 pl-3 border-l-2 border-gray-400">
                        {translatedData.translatedContent}
                    </p>
                )}
            </div>

            {/* 첨부된 비디오 */}
            {diary.diaryVideos && diary.diaryVideos.length > 0 && (
                <div className="mb-2">
                    {diary.diaryVideos.map((diaryVideo: any, index: number) => (
                        <VideoPlayer
                            key={index}
                            video={diaryVideo.video}
                            className="w-full rounded-lg"
                        />
                    ))}
                </div>
            )}

            {/* 첨부된 이미지 */}
            {diary.diaryImages && diary.diaryImages.length > 0 && (
                <div className="mb-2">
                    <div className="flex gap-1 overflow-x-auto pb-1">
                        {diary.diaryImages.map((diaryImage: any, index: number) => (
                            <div
                                key={index}
                                className="relative flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onImageClick(diary.diaryImages, index);
                                }}
                            >
                                <img
                                    src={getImageUrl(diaryImage.image, 120, 120)}
                                    alt={`Diary image ${index + 1}`}
                                    className="w-16 h-16 object-cover rounded-lg"
                                    onError={(e) => {
                                        const target = e.target as HTMLImageElement;
                                        target.src = "/imgs/dummy/basic-img.png";
                                    }}
                                />
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* 다이어리 메타 정보 영역 */}
            <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                <div className="flex items-center space-x-4">
                    {/* 좋아요 버튼 */}
                    <button
                        className={`flex items-center space-x-1 transition-colors duration-200 ${isLiking
                            ? 'opacity-50 cursor-not-allowed'
                            : 'hover:text-[#CF3688]'
                            }`}
                        onClick={(e) => {
                            e.stopPropagation();
                            onLikeToggle(diary.id);
                        }}
                        disabled={isLiking}
                    >
                        {isLiking ? (
                            // 로딩 중일 때 스피너 표시
                            <svg className="w-4 h-4 text-gray-400 animate-spin" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                        ) : (
                            // 좋아요 아이콘
                            <svg 
                                className={`w-4 h-4 ${diary.isLiked ? 'fill-[#CF3688] text-[#CF3688]' : 'fill-none'}`} 
                                stroke="currentColor" 
                                viewBox="0 0 24 24"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                            </svg>
                        )}
                        <span className="text-xs font-medium text-gray-400">
                            {diary.likeCount || 0}
                        </span>
                    </button>
                    
                    {/* 댓글 수 */}
                    <div className="flex items-center space-x-1">
                        <img src="/imgs/icons/icon-comment.svg" alt="comment" className="w-4 h-4" />
                        <span className="text-xs font-medium text-gray-400">
                            {diary.commentCount || 0}
                        </span>
                    </div>
                    
                    {/* 북마크 버튼 */}
                    <button
                        className={`flex items-center space-x-1 transition-colors duration-200 ${isBookmarking
                            ? 'opacity-50 cursor-not-allowed'
                            : 'hover:text-[#CF3688]'
                            }`}
                        onClick={(e) => {
                            e.stopPropagation();
                            onBookmarkToggle(diary.id);
                        }}
                        disabled={isBookmarking}
                    >
                        {isBookmarking ? (
                            // 로딩 중일 때 스피너 표시
                            <svg className="w-4 h-4 text-gray-400 animate-spin" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                        ) : (
                            // 북마크 아이콘
                            <svg 
                                className={`w-4 h-4 ${diary.isBookmarked ? 'fill-[#CF3688] text-[#CF3688]' : 'fill-none'}`} 
                                stroke="currentColor" 
                                viewBox="0 0 24 24"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 3H7a2 2 0 00-2 2v16l7-3 7 3V5a2 2 0 00-2-2z" />
                            </svg>
                        )}
                        <span className="text-xs font-medium text-gray-400">
                            {diary.bookmarkCount || 0}
                        </span>
                    </button>
                </div>
                
                {/* 자세히보기 링크 */}
                <a
                    href="#"
                    className="flex items-center space-x-1 text-[#CF3688] text-xs font-medium hover:text-[#B8327A] transition-colors duration-200 underline decoration-dotted underline-offset-2"
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onDiaryDetailPopup(diary.id);
                    }}
                >
                    <span>자세히보기</span>
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                    </svg>
                </a>
            </div>
        </div>
    );
};

const BookmarkDiary = () => {
    const navigate = useNavigate();
    const { showPopup } = usePopup();
    const [diaryList, setDiaryList] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
    const [hasMore, setHasMore] = useState<boolean>(true);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const LIMIT = 10;

    // 번역 관련 상태
    const [translatingDiaryIds, setTranslatingDiaryIds] = useState<Set<number>>(new Set());
    const [translatedDiaries, setTranslatedDiaries] = useState<Map<number, ITranslateResponse>>(new Map());

    // 좋아요 토글 로딩 상태
    const [likingDiaryIds, setLikingDiaryIds] = useState<Set<number>>(new Set());
    
    // 북마크 토글 로딩 상태
    const [bookmarkingDiaryIds, setBookmarkingDiaryIds] = useState<Set<number>>(new Set());

    // 다이어리 목록 조회
    const fetchBookmarkedDiaries = useCallback(async () => {
        try {
            setIsLoading(true);
            console.log("🔄 첫 번째 페이지 북마크 다이어리 목록 조회 시작");
            const result = await getBookmarkedDiaries(1);
            console.log("📊 첫 번째 페이지 API 응답:", result);

            if (result?.diaryList) {
                setDiaryList(result.diaryList);
                setHasMore(result.pagination?.hasNextPage || false);
                setCurrentPage(1);
                console.log("✅ 첫 번째 페이지 데이터 설정 완료", {
                    itemCount: result.diaryList.length,
                    hasNextPage: result.pagination?.hasNextPage
                });
            } else {
                console.log("⚠️ 북마크 다이어리 목록이 비어있음");
                setDiaryList([]);
                setHasMore(false);
            }
        } catch (error) {
            console.error("❌ 북마크 다이어리 목록 조회 실패:", error);
            setDiaryList([]);
            setHasMore(false);
        } finally {
            setIsLoading(false);
        }
    }, []);

    // 추가 데이터 로드 (무한 스크롤용)
    const loadMoreData = useCallback(async () => {
        if (isLoadingMore || !hasMore) {
            console.log("🚫 loadMoreData 호출 차단:", { isLoadingMore, hasMore, currentPage });
            return;
        }

        try {
            setIsLoadingMore(true);
            const nextPage = currentPage + 1;
            console.log("📄 다음 페이지 로드 시작:", nextPage);

            const result = await getBookmarkedDiaries(nextPage);
            console.log("📊 다음 페이지 API 응답:", { page: nextPage, result });

            if (result?.diaryList && result.diaryList.length > 0) {
                setDiaryList(prevList => {
                    const newList = [...prevList, ...result.diaryList];
                    console.log("📊 업데이트된 다이어리 데이터:", {
                        previousCount: prevList.length,
                        newCount: result.diaryList.length,
                        totalCount: newList.length,
                        hasNextPage: result.pagination?.hasNextPage
                    });
                    return newList;
                });
                setCurrentPage(nextPage);
                setHasMore(result.pagination?.hasNextPage || false);
                console.log("✅ 페이지 상태 업데이트:", {
                    newPage: nextPage,
                    hasMore: result.pagination?.hasNextPage || false
                });
            } else {
                console.log("📝 더 이상 로드할 데이터가 없음 (빈 배열 또는 null)");
                setHasMore(false);
            }
        } catch (error) {
            console.error("❌ 추가 데이터 로드 실패:", error);
            setHasMore(false);
        } finally {
            setIsLoadingMore(false);
            console.log("🔄 로딩 상태 해제");
        }
    }, [currentPage, hasMore, isLoadingMore]);

    // 무한 스크롤 이벤트 핸들러
    useEffect(() => {
        let isScrolling = false; // 스크롤 중복 방지 플래그
        let scrollTimeout: NodeJS.Timeout;

        const handleScroll = () => {
            // 이미 스크롤 처리 중이거나 로딩 중이거나 다음 페이지가 없으면 무시
            if (isScrolling || isLoadingMore || !hasMore) {
                console.log("🚫 스크롤 이벤트 무시:", { isScrolling, isLoadingMore, hasMore });
                return;
            }

            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
            const windowHeight = window.innerHeight;
            const documentHeight = document.documentElement.scrollHeight;
            const scrollPosition = scrollTop + windowHeight;
            const triggerPosition = documentHeight - 200; // 200px 여유

            // 스크롤이 하단 근처에 도달했는지 확인
            if (scrollPosition >= triggerPosition) {
                console.log("🔄 스크롤 임계값 도달, 다음 페이지 로드 시작");
                isScrolling = true; // 스크롤 처리 시작

                loadMoreData().finally(() => {
                    // API 호출 완료 후 플래그 리셋 (1초 후)
                    clearTimeout(scrollTimeout);
                    scrollTimeout = setTimeout(() => {
                        isScrolling = false;
                        console.log("🔄 스크롤 플래그 리셋");
                    }, 1000);
                });
            }
        };

        // 스크롤 이벤트 리스너 등록
        window.addEventListener('scroll', handleScroll, { passive: true });
        console.log("👂 스크롤 이벤트 리스너 등록 완료");

        return () => {
            window.removeEventListener('scroll', handleScroll);
            clearTimeout(scrollTimeout);
            console.log("🗑️ 스크롤 이벤트 리스너 제거");
        };
    }, [isLoadingMore, hasMore, loadMoreData]);

    useEffect(() => {
        fetchBookmarkedDiaries();
    }, [fetchBookmarkedDiaries]);

    const handleDiaryClick = (diaryId: number) => {
        navigate(`/agency/diary/${diaryId}`);
    };

    const handleDiaryDetailPopup = (diaryId: number) => {
        showPopup(
            <StudentDiaryDetailPopup
                diaryId={diaryId.toString()}
                onClose={() => showPopup(null)}
            />
        );
    };

    const handleLikeToggle = async (diaryId: number) => {
        // 이미 좋아요 처리 중인 다이어리인지 확인
        if (likingDiaryIds.has(diaryId)) {
            console.log("🚫 이미 좋아요 처리 중인 다이어리:", diaryId);
            return;
        }

        try {
            // 좋아요 처리 시작
            setLikingDiaryIds(prev => new Set(prev).add(diaryId));
            console.log("❤️ 좋아요 토글 시작:", diaryId);

            const response: ILikeToggleResponse = await toggleDiaryLike(diaryId.toString());
            console.log("좋아요 토글 결과:", response);
            
            // 다이어리 목록에서 해당 다이어리 업데이트
            setDiaryList(prevList => 
                prevList.map(diary => 
                    diary.id === diaryId 
                        ? { 
                            ...diary, 
                            isLiked: response.isLiked, 
                            likeCount: response.likeCount 
                        }
                        : diary
                )
            );
        } catch (error) {
            console.error("좋아요 토글 실패:", error);
            // 에러 처리 (필요시 사용자에게 알림)
        } finally {
            // 좋아요 처리 완료 후 ID 제거
            setLikingDiaryIds(prev => {
                const newSet = new Set(prev);
                newSet.delete(diaryId);
                return newSet;
            });
            console.log("✅ 좋아요 토글 완료:", diaryId);
        }
    };

    const handleBookmarkToggle = async (diaryId: number) => {
        // 이미 북마크 처리 중인 다이어리인지 확인
        if (bookmarkingDiaryIds.has(diaryId)) {
            console.log("🚫 이미 북마크 처리 중인 다이어리:", diaryId);
            return;
        }

        try {
            // 북마크 처리 시작
            setBookmarkingDiaryIds(prev => new Set(prev).add(diaryId));
            console.log("🔖 북마크 토글 시작:", diaryId);

            const response: IBookmarkToggleResponse = await toggleDiaryBookmark(diaryId.toString());
            console.log("북마크 토글 결과:", response);
            
            // 다이어리 목록에서 해당 다이어리 업데이트
            setDiaryList(prevList => 
                prevList.map(diary => 
                    diary.id === diaryId 
                        ? { 
                            ...diary, 
                            isBookmarked: response.isBookmarked, 
                            bookmarkCount: response.bookmarkCount 
                        }
                        : diary
                )
            );
        } catch (error) {
            console.error("북마크 토글 실패:", error);
            // 에러 처리 (필요시 사용자에게 알림)
        } finally {
            // 북마크 처리 완료 후 ID 제거
            setBookmarkingDiaryIds(prev => {
                const newSet = new Set(prev);
                newSet.delete(diaryId);
                return newSet;
            });
            console.log("✅ 북마크 토글 완료:", diaryId);
        }
    };

    // 번역 처리
    const handleTranslate = async (diaryId: number) => {
        if (translatingDiaryIds.has(diaryId) || translatedDiaries.has(diaryId)) {
            console.log("🚫 이미 번역 처리 중이거나 번역된 다이어리:", diaryId);
            return;
        }

        try {
            setTranslatingDiaryIds(prev => new Set(prev).add(diaryId));
            console.log("🌐 번역 시작:", diaryId);

            const result = await translateDiary(diaryId.toString());
            console.log("번역 결과:", result);

            setTranslatedDiaries(prev => new Map(prev).set(diaryId, result));

        } catch (error) {
            console.error("번역 실패:", error);
            // 에러 처리 (필요시 사용자에게 알림)
        } finally {
            setTranslatingDiaryIds(prev => {
                const newSet = new Set(prev);
                newSet.delete(diaryId);
                return newSet;
            });
            console.log("✅ 번역 완료:", diaryId);
        }
    };

    const handleStudentClick = (userStudentId: number) => {
        navigate(`/agency/student/${userStudentId}`);
    };

    const handleStudentInfoPopup = (userStudentId: number) => {
        showPopup(
            <StudentProfilePopup
                userStudentId={userStudentId}
                onClose={() => {}}
            />
        );
    };

    const handleImageClick = (diaryImages: any[], initialIndex: number) => {
        // diaryImages에서 image 객체들만 추출
        const imageInfoList = diaryImages.map(diaryImage => diaryImage.image);
        
        showPopup(
            <ImageSlidePopup
                imageInfoList={imageInfoList}
                initialSlideIdx={initialIndex}
                onClose={() => showPopup(null)}
            />
        );
    };

    const groupedDiaries = groupDiariesByDate(diaryList);

    return (
        <main className="bg-gray-50 min-h-screen overflow-y-auto">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 pt-6">
                <h1 className="text-3xl font-bold text-gray-900 mb-6">
                    북마크된 다이어리
                </h1>
                <p className="text-gray-500 mb-6">
                    북마크한 다이어리의 목록입니다.
                </p>
            </div>
            <div className="container mx-auto px-3 sm:px-4 lg:px-6 pt-4">
                <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                    <div className="p-4">
                        {isLoading ? (
                            <div className="space-y-5 -mx-2">
                                {Array.from({ length: 5 }, (_, index) => (
                                    <div key={`diary-skeleton-${index}`}>
                                        <SkeletonLoading type="diary-card" count={1} />
                                    </div>
                                ))}
                            </div>
                        ) : diaryList?.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-6 text-gray-500">
                                <svg className="w-12 h-12 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M17 3H7a2 2 0 00-2 2v16l7-3 7 3V5a2 2 0 00-2-2z" />
                                </svg>
                                <p className="text-center">
                                    북마크한 다이어리가 없습니다.
                                </p>
                            </div>
                        ) : (
                            <div>
                                <div className="space-y-5 -mx-2">
                                    {groupedDiaries.map((group, groupIndex) => (
                                        <div key={group.date}>
                                            {/* 날짜 헤더 */}
                                            <DateHeader date={group.date} />
                                            
                                            {/* 해당 날짜의 다이어리들 */}
                                            <div className="space-y-3">
                                                {group.diaries.map((diary) => {
                                                    const isTranslating = translatingDiaryIds.has(diary.id);
                                                    const translatedData = translatedDiaries.get(diary.id);
                                                    const isLiking = likingDiaryIds.has(diary.id);
                                                    const isBookmarking = bookmarkingDiaryIds.has(diary.id);

                                                    return (
                                                        <DiaryItem
                                                            key={diary.id}
                                                            diary={diary}
                                                            onStudentInfoPopup={handleStudentInfoPopup}
                                                            onImageClick={handleImageClick}
                                                            onDiaryDetailPopup={handleDiaryDetailPopup}
                                                            onLikeToggle={handleLikeToggle}
                                                            onBookmarkToggle={handleBookmarkToggle}
                                                            isTranslating={isTranslating}
                                                            translatedData={translatedData}
                                                            onTranslate={handleTranslate}
                                                            isLiking={isLiking}
                                                            isBookmarking={isBookmarking}
                                                        />
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {/* 추가 로딩 상태 */}
                                {isLoadingMore && (
                                    <div className="space-y-5 -mx-2">
                                        {Array.from({ length: 3 }, (_, index) => (
                                            <div key={`diary-skeleton-more-${index}`}>
                                                <SkeletonLoading type="diary-card" count={1} />
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* 더 이상 로드할 데이터가 없을 때 */}
                                {!isLoadingMore && !hasMore && diaryList.length > 0 && (
                                    <div className="flex justify-center items-center py-8">
                                        <div className="text-center">
                                            <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                                                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </main>
    );
};

export default BookmarkDiary;
